import {
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  Renderer2,
  booleanAttribute
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type HtxTooltipPosition = 'top' | 'bottom' | 'left' | 'right';

@Directive({
  selector: '[htxTooltip]',
  standalone: true
})
export class TooltipDirective implements OnDestroy {
  @Input('htxTooltip') text = '';
  @Input() htxTooltipPosition: HtxTooltipPosition = 'top';
  @Input({ transform: booleanAttribute }) htxTooltipMultiline = false;
  @Input() htxTooltipShowDelay = 150;
  @Input() htxTooltipHideDelay = 0;

  private tooltipEl: HTMLElement | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly isBrowser: boolean;

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  @HostListener('mouseenter') onMouseEnter(): void { this.scheduleShow(); }
  @HostListener('mouseleave') onMouseLeave(): void { this.scheduleHide(); }
  @HostListener('focusin')    onFocusIn(): void    { this.scheduleShow(); }
  @HostListener('focusout')   onFocusOut(): void   { this.scheduleHide(); }

  ngOnDestroy(): void {
    this.clearTimers();
    this.destroyTooltip();
  }

  private scheduleShow(): void {
    if (!this.isBrowser || !this.text) return;
    this.clearTimers();
    this.showTimer = setTimeout(() => this.show(), this.htxTooltipShowDelay);
  }

  private scheduleHide(): void {
    if (!this.isBrowser) return;
    this.clearTimers();
    this.hideTimer = setTimeout(() => this.destroyTooltip(), this.htxTooltipHideDelay);
  }

  private clearTimers(): void {
    if (this.showTimer) { clearTimeout(this.showTimer); this.showTimer = null; }
    if (this.hideTimer) { clearTimeout(this.hideTimer); this.hideTimer = null; }
  }

  private show(): void {
    if (this.tooltipEl || !this.text) return;

    const el = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(el, 'htx-tooltip');
    this.renderer.addClass(el, `htx-tooltip--${this.htxTooltipPosition}`);
    if (this.htxTooltipMultiline) {
      this.renderer.addClass(el, 'htx-tooltip--multiline');
    }
    this.renderer.setAttribute(el, 'role', 'tooltip');
    el.textContent = this.text;
    this.renderer.appendChild(document.body, el);
    this.tooltipEl = el;
    this.position();
  }

  private position(): void {
    if (!this.tooltipEl) return;
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const tipRect = this.tooltipEl.getBoundingClientRect();
    const offset = 8;
    let top = 0;
    let left = 0;

    switch (this.htxTooltipPosition) {
      case 'bottom':
        top = hostRect.bottom + offset;
        left = hostRect.left + (hostRect.width - tipRect.width) / 2;
        break;
      case 'left':
        top = hostRect.top + (hostRect.height - tipRect.height) / 2;
        left = hostRect.left - tipRect.width - offset;
        break;
      case 'right':
        top = hostRect.top + (hostRect.height - tipRect.height) / 2;
        left = hostRect.right + offset;
        break;
      case 'top':
      default:
        top = hostRect.top - tipRect.height - offset;
        left = hostRect.left + (hostRect.width - tipRect.width) / 2;
        break;
    }

    const margin = 4;
    const maxLeft = window.innerWidth - tipRect.width - margin;
    const maxTop = window.innerHeight - tipRect.height - margin;
    left = Math.max(margin, Math.min(left, maxLeft));
    top = Math.max(margin, Math.min(top, maxTop));

    this.renderer.setStyle(this.tooltipEl, 'top', `${top + window.scrollY}px`);
    this.renderer.setStyle(this.tooltipEl, 'left', `${left + window.scrollX}px`);
  }

  private destroyTooltip(): void {
    if (this.tooltipEl?.parentNode) {
      this.tooltipEl.parentNode.removeChild(this.tooltipEl);
    }
    this.tooltipEl = null;
  }
}
